import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Container,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Paper,
    Stack,
    Typography,
    TextField,
    useMediaQuery,
    useTheme
} from "@mui/material";
import RedeemIcon from "@mui/icons-material/Redeem";
import LuggageIcon from "@mui/icons-material/Luggage";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

import nightTrain from "assets/img/travel/night-train.jpg";
import velikaPlanina from "assets/img/travel/velika-planina.jpg";
import car from "assets/img/travel/car.avif";
import vintgar from "assets/img/travel/vintgar.webp";
import gardenVillage from "assets/img/travel/garden-village.jpg";
import postman from "assets/img/travel/postman.jpg";
import rafting from "assets/img/travel/rafting.avif";
import tolmin from "assets/img/travel/tolmin.jpg";
import { useRibRequest } from "../utils/function.utils";

const itemData = [
    { img: nightTrain, title: "Prendre le train de nuit" },
    { img: car, title: "Louer une voiture (ce modèle exactement)" },
    { img: velikaPlanina, title: "Dormir dans un chalet à Velika Planina" },
    { img: vintgar, title: "Visiter les Gorges de Vintgar" },
    { img: gardenVillage, title: "Dormir au Garden Village (Super luxe)" },
    { img: postman, title: "Dormir en haut du Col Vršič" },
    { img: rafting, title: "Faire du rafting dans la Vallée de la Soca" },
    { img: tolmin, title: "Visiter les Gorges de Tolmin" },
];

const WishList: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { ribRequested, isLoading, requestRib } = useRibRequest();
    const [email, setEmail] = useState("");
    const [showEmailField, setShowEmailField] = useState(false);
    const [emailError, setEmailError] = useState("");

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleRequestRib = async () => {
        if (!email.trim()) {
            setEmailError("Veuillez entrer votre adresse email");
            return;
        }
        if (!validateEmail(email)) {
            setEmailError("Veuillez entrer une adresse email valide");
            return;
        }
        setEmailError("");

        await requestRib({
            guestNames: "",
            amount: "",
            pageOrigin: "Liste de mariage",
            email: email,
        });
    };

    return (
        <>
            <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
                Liste de mariage
            </Typography>

            {/* ===== CAGNOTTE ===== */}
            <Container maxWidth="md" sx={{ mb: 4 }}>
                <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                            <RedeemIcon sx={{ mr: 2, color: "primary.main", fontSize: 30 }} />
                            <Typography variant="h5">
                                Avons-nous une liste de mariage ?
                            </Typography>
                        </Box>

                        <Typography sx={{ mb: 1 }}>
                            Nous n'avons pas une liste de mariage à proprement parler, cependant
                            nous avons une cagnotte pour nous aider à financer notre{" "}
                            <strong>voyage de noces</strong>.
                        </Typography>

                        <Typography sx={{ mb: 3 }}>
                            Vous pouvez participer via les moyens de paiement ci-dessous. Une boîte sera
                            également disponible sur le lieu de la réception.
                        </Typography>

                        <Stack spacing={2} alignItems="center">
                            {/* PayPal */}
                            <Button
                                variant="contained"
                                href="https://paypal.me/ThibaultQuoy"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    bgcolor: "#0067C5",
                                    borderRadius: "9999px",
                                    textTransform: "none",
                                    py: 1.25,
                                    px: 3,
                                    minWidth: 220,
                                    alignItems: "center",
                                    gap: 0.8,
                                    "&:hover": {
                                        bgcolor: "#005a94",
                                    },
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{ color: "white", fontWeight: 500, fontSize: "1rem" }}
                                >
                                    Participer avec
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontStyle: "italic",
                                        fontWeight: "bold",
                                        color: "white",
                                        fontSize: "1rem",
                                        fontFamily: '"PayPal Bold", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                    }}
                                >
                                    PayPal
                                </Typography>
                            </Button>

                            {/* Bouton pour afficher le champ email */}
                            {!showEmailField && !ribRequested && (
                                <Button
                                    variant="contained"
                                    onClick={() => setShowEmailField(true)}
                                    sx={{
                                        borderRadius: "9999px",
                                        textTransform: "none",
                                        py: 1.25,
                                        px: 3,
                                        minWidth: 220,
                                        fontWeight: 500,
                                        fontSize: "1rem",
                                    }}
                                >
                                    Participer par virement
                                </Button>
                            )}

                            {/* Champ email (affiché uniquement si showEmailField est true) */}
                            {showEmailField && !ribRequested && (
                                <>
                                    <TextField
                                        label="Votre email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (emailError) setEmailError("");
                                        }}
                                        placeholder="exemple@email.com"
                                        fullWidth
                                        disabled={isLoading}
                                        sx={{ maxWidth: 400 }}
                                        helperText={emailError || "Nous vous enverrons le RIB à cette adresse"}
                                        error={!!emailError}
                                    />
                                    <Button
                                        variant="contained"
                                        color={ribRequested ? "success" : "primary"}
                                        onClick={handleRequestRib}
                                        disabled={isLoading}
                                        sx={{
                                            borderRadius: "9999px",
                                            textTransform: "none",
                                            py: 1.25,
                                            px: 3,
                                            minWidth: 220,
                                            fontWeight: 500,
                                            fontSize: "1rem",
                                        }}
                                    >
                                        {isLoading ? 'Envoi en cours...' : 'Demander le RIB'}
                                    </Button>
                                </>
                            )}

                            {ribRequested && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Nous avons bien reçu votre demande et vous enverrons le RIB à <strong>{email}</strong> dès que possible ! <br />Merci beaucoup !
                                </Alert>
                            )}
                        </Stack>
                    </Box>
                </Paper>
            </Container>

            {/* ===== VOYAGE ===== */}
            <Container maxWidth="md" sx={{ mb: 4 }}>
                <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                            <LuggageIcon sx={{ mr: 2, color: "primary.main", fontSize: 30 }} />
                            <Typography variant="h5">
                                Et ce voyage de noces, il sera où ?
                            </Typography>
                        </Box>

                        <Typography sx={{ mb: 1 }}>
                            Notre objectif est de partir en <strong>Slovénie</strong> !
                        </Typography>

                        <Typography sx={{ mb: 3 }}>
                            Voici un aperçu de ce que votre participation pourrait nous permettre
                            de faire :
                        </Typography>

                        {/* ===== GALERIE ===== */}
                        {isMobile ? (
                            /* ----- MOBILE : CAROUSEL ----- */
                            <Swiper
                                modules={[Pagination]}
                                spaceBetween={16}
                                slidesPerView={1.07}
                                pagination={{ clickable: true }}
                                style={{
                                    paddingBottom: 32,
                                    "--swiper-pagination-color": theme.palette.primary.main,
                                    "--swiper-pagination-bullet-inactive-color":
                                    theme.palette.primary.light,
                                    "--swiper-pagination-bullet-inactive-opacity": "0.4",
                                }}
                            >
                                {itemData.map((item) => (
                                    <SwiperSlide key={item.img}>
                                        <Paper
                                            elevation={3}
                                            sx={{ borderRadius: 3, overflow: "hidden" }}
                                        >
                                            <img
                                                src={item.img}
                                                alt={item.title}
                                                style={{
                                                    width: "100%",
                                                    height: 220,
                                                    objectFit: "cover",
                                                }}
                                            />
                                            <Box sx={{ p: 1.5 }}>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {item.title}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        ) : (
                            /* ----- DESKTOP : AFFICHAGE ORIGINAL + COINS ARRONDIS ----- */
                            <ImageList>
                                {itemData.map((item) => (
                                    <ImageListItem key={item.img}>
                                        <img
                                            srcSet={`${item.img}?w=248&fit=crop&auto=format&dpr=2 2x`}
                                            src={`${item.img}?w=248&fit=crop&auto=format`}
                                            alt={item.title}
                                            loading="lazy"
                                            style={{ borderRadius: 12 }}
                                        />
                                        <ImageListItemBar
                                            title={item.title}
                                            position="below"
                                        />
                                    </ImageListItem>
                                ))}
                            </ImageList>
                        )}
                    </Box>
                </Paper>
            </Container>
        </>
    );
};

export default WishList;
